import styles from "./userModal.module.scss";
import { ModalWindow } from "../../shared/components/modalWindow/modalWindow";
import { User } from "../../types/user.types";
import { useTranslation } from "react-i18next";
import { Input } from "../../shared/components/input/input";
import {
  Dropdown,
  DropdownItem
} from "../../shared/components/dropdown/dropdown";
import {
  useEffect,
  useMemo,
  useState
} from "react";
import { useGetAllRolesQuery } from "../../api/services/role.service";
import {
  array,
  boolean,
  date,
  number,
  object,
  string
} from "yup";
import {
  useFieldArray,
  useForm, useWatch
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import addBlockIcon from "../../shared/images/addBlock.svg";
import removeBlockIcon from "../../shared/images/removeBlock.svg";
import {
  useGetAllProjectsQuery,
  useGetUserProjectsQuery
} from "../../api/services/project.service";
import {
  useCreateUserMutation,
  useDeleteUserMutation,
  useUpdateUserMutation
} from "../../api/services/user.service";
import { MessageWindow } from "../../shared/components/messageWindow/messageWindow";
import { DateInput } from "../../shared/components/dateInput/dateInput";
import { Project } from "../../types/project.types";
import { CheckboxHookForm } from "../../shared/components/checkboxHookForm/checkboxHookForm";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { usePrevious } from "../../hooks/usePrevious";

type UserModalProps = {
  onClose: (isSave: boolean) => void;
  user?: User
}

export const UserModal = ({ onClose, user }: UserModalProps) => {
  const { t } = useTranslation();

  const { isTestToolAdmin } = useSelector((state: RootState) => state.authReducer);

  const { data: roles } = useGetAllRolesQuery("");
  const { data: projects } = useGetAllProjectsQuery("");
  const { data: userProjects } = useGetUserProjectsQuery("");

  const [projectDropdowns, setProjectDropdowns] = useState<Array<Array<DropdownItem>>>([]);

  const [showDeleteMessage, setShowDeleteMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);

  const [needChangePassword, setNeedChangePassword] = useState(!user?.id);

  const [createUserMutation, { data: createData, isSuccess: isSuccessCreate, isLoading: isLoadingCreate, error: errorCreate }] = useCreateUserMutation();
  const [updateUserMutation, { isSuccess: isSuccessUpdate, isLoading: isLoadingUpdate, error: errorUpdate }] = useUpdateUserMutation();
  const [deleteUserMutation, { isSuccess: isSuccessDelete, isLoading: isLoadingDelete, error: errorDelete }] = useDeleteUserMutation();

  useEffect(() => {
    if (errorCreate || errorUpdate || errorDelete) {
      setShowErrorMessage(true)
    }
  }, [errorCreate, errorUpdate, errorDelete]);

  useEffect(() => {
    if (isSuccessCreate && createData) {
      onClose(true);
    }
  }, [createData, isSuccessCreate, onClose]);

  useEffect(() => {
    if (isSuccessUpdate) {
      onClose(true);
    }
  }, [isSuccessUpdate, onClose]);

  useEffect(() => {
    if (isSuccessDelete) {
      onClose(true);
    }
  }, [isSuccessDelete, onClose]);

  const initialSchema = object({
    login: string().required(t("common.notValid")).typeError(t("common.notValid")),
    email: string().required(t("common.notValid")).typeError(t("common.notValid")),
    fullName: string().required(t("common.notValid")).typeError(t("common.notValid")),
    password: needChangePassword && (!user?.id || isTestToolAdmin)
      ? string().required(t("common.notValid")).typeError(t("common.notValid"))
      : string().typeError(t("common.notValid")),
    projects: array().required().of(
      object().shape({
        projectId: number().required(t("common.notValid")),
        roleId: number().required(t("common.notValid")).test(
          'is-not-zero',
          t("common.notValid"),
          value => value !== 0
        ),
        passwordExpirationDate: date().required(),
        isActive: boolean().required()
      })
    )
  });

  const getDefaultDate = (expirationDate?: string): Date => {
    const passwordExpirationDate = !!expirationDate ? new Date(expirationDate) : new Date();
    !expirationDate && passwordExpirationDate.setMonth(passwordExpirationDate.getMonth() + 6);
    return passwordExpirationDate;
  }

  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(initialSchema),
    defaultValues: {
      ...user,
      projects: user?.projects.map(project => ({
        projectId: project.projectId,
        roleId: project.roleId,
        passwordExpirationDate: getDefaultDate(project.passwordExpirationDate),
        isActive: project.isActive
      })) || [{ isActive: true, passwordExpirationDate: getDefaultDate(), roleId: 0 }],
    },
    mode: "onChange"
  });

  const {
    fields: projectFields,
    append: appendProject,
    remove: removeProject
  } = useFieldArray({ control, name: "projects" });
  const projectFieldsWatch = useWatch({ control, name: "projects" });
  const previousProjectFieldsWatch = usePrevious(projectFieldsWatch);

  useEffect(() => {
    if (projectFieldsWatch.length !== previousProjectFieldsWatch?.length) {
      updateProjectDropdowns();
    } else {
      const isChangeProjectId = projectFieldsWatch.some(projectField => {
        return !previousProjectFieldsWatch.find(item => item.projectId === projectField.projectId);
      });
      if (isChangeProjectId) {
        updateProjectDropdowns();
      }
    }
  }, [projectFieldsWatch, userProjects]);

  useEffect(() => {
    const adminTestToolRole = roles?.find(item => item.isTestToolAdmin);
    if (adminTestToolRole) {
      const currentIsIncludeAdminRole = projectFieldsWatch.some(item => item.roleId === adminTestToolRole?.id);
      const previousIsIncludeAdminRole = previousProjectFieldsWatch?.some(item => item.roleId === adminTestToolRole?.id) || false;
      if (currentIsIncludeAdminRole) {
        if (!previousIsIncludeAdminRole) {
          const newProjects = projectFieldsWatch.map(item => ({
            ...item,
            roleId: adminTestToolRole.id
          }));
          projects?.forEach(project => {
            const findProject = projectFieldsWatch.find(item => item.projectId === project.id);

            if (!findProject) {
              newProjects.push({
                projectId: project.id,
                roleId: adminTestToolRole.id,
                passwordExpirationDate: getDefaultDate(),
                isActive: true
              })
            }
          });
          setValue(`projects`, newProjects);
          clearErrors();
        } else {
          const currentIncludeNotAdminRole = projectFieldsWatch.find(item => item.roleId !== adminTestToolRole.id);

          if (!!currentIncludeNotAdminRole) {
            setValue("projects", projectFieldsWatch.map(project => ({
              ...project,
              roleId: currentIncludeNotAdminRole.roleId
            })));
          } else if (projects?.length !== projectFieldsWatch.length) {
            const notAdminRole = roles?.find(role => !role.isTestToolAdmin)
            if (notAdminRole) {
              setValue("projects", projectFieldsWatch.map(item => ({
                ...item,
                roleId: notAdminRole.id
              })))
            }
          }
        }
      }
    }
  }, [projectFieldsWatch, roles, projects]);

  const updateProjectDropdowns = (): void => {
    const newArray: Array<Array<DropdownItem>> = [];
    projectFieldsWatch.forEach(projectField => {
      const projectId = projectField.projectId;
      if (userProjects?.map(item => item.id).includes(projectId)) {
        const projectIds = projectFieldsWatch.map(item => item.projectId);
        const filteredProjects = userProjects?.filter(project => {
          return projectId === project.id || !projectIds.includes(project.id);
        });
        newArray.push(filteredProjects?.map(getDropdownItem) || []);
      } else {
        if (!user?.id) {
          newArray.push(userProjects?.map(getDropdownItem) || []);
        } else {
          newArray.push(projects?.map(getDropdownItem) || []);
        }
      }
    });
    setProjectDropdowns(newArray);
  }

  const onDeleteClick = () => {
    setShowDeleteMessage(true);
  }

  const closeMessageWindow = (isConfirm: boolean): void => {
    if (isConfirm) {
      deleteUserMutation(user?.id as number);
    }
    setShowDeleteMessage(false);
  }

  const closeErrorMessage = (): void => {
    setShowErrorMessage(false);
  }

  const onClickSave = () => {
    handleSubmit((value) => {
      const newUser: User = {
        id: user?.id,
        login: value.login,
        password: needChangePassword ? value.password : undefined,
        email: value.email,
        fullName: value.fullName,
        projects: value.projects?.map(project => ({
          projectId: project.projectId,
          roleId: project.roleId,
          passwordExpirationDate: project.passwordExpirationDate.toISOString(),
          isActive: project.isActive
        })) || []
      }
      if (!!user?.id) {
        updateUserMutation(newUser);
      } else {
        createUserMutation(newUser);
      }
    })();
  }

  const addProjectClick = (): void => {
    if (!!firstFreeProject) {
      appendProject({
        projectId: firstFreeProject.id,
        passwordExpirationDate: getDefaultDate(),
        isActive: true,
        roleId: 0
      });
    }
  }

  const getDropdownItem = (item: Project) => {
    return {
      id: item.id,
      name: item.name,
      title: item.name
    }
  }

  const onClickRemoveProject = (indexProject: number): void => {
    const newProjectDropdown = [...projectDropdowns];
    newProjectDropdown.splice(indexProject, 1);
    setProjectDropdowns(newProjectDropdown);
    removeProject(indexProject);
  }

  const isEnabledEditProject = (indexProject: number): boolean => {
    const projectId = projectFieldsWatch[indexProject]?.projectId;
    return !projectId || userProjects?.map(item => item.id).includes(projectId) || false;
  }

  const rolesDropdown: DropdownItem[] = useMemo(() => {
    return roles?.map(item => ({
      id: item.id,
      name: item.name,
      title: item.name
    })) || [];
  }, [roles]);

  const firstFreeProject = useMemo(() => {
    const projectIds = projectFieldsWatch.map(item => item.projectId);
    return userProjects?.find(project => {
      return !projectIds.includes(project.id)
    });
  }, [projectFieldsWatch, userProjects]);

  return (
    <>
      <ModalWindow title={!!user?.id ? t("user.edit") : t("user.add")}
                   onClose={() => onClose(false)}
                   width={814}
                   children={
                     <div className={styles.mainContainer}>
                       <div className={styles.container}>
                         <div className={styles.subContainer}>
                           <div className={styles.inputs}>
                             <div className={styles.inputContainer}>
                               <span className={styles.required}>{t("login.login")}</span>
                               <div className={styles.input}>
                                 <Input name={"login"}
                                        control={control}
                                        disabled={!!user?.id && !isTestToolAdmin}
                                        maxLength={100}
                                        error={!!errors.login ? errors.login.message : ""}/>
                               </div>
                             </div>
                             <div className={styles.inputContainer}>
                               <span className={styles.required}>{t("admin.fullName")}</span>
                               <div className={styles.input}>
                                 <Input control={control}
                                        name={"fullName"}
                                        disabled={!!user?.id && !isTestToolAdmin}
                                        maxLength={100}
                                        error={!!errors.fullName ? errors.fullName.message : ""}/>
                               </div>
                             </div>
                           </div>
                           <div className={styles.inputs}>
                             <div className={styles.inputContainer}>
                               <span className={styles.required}>{t("admin.email")}</span>
                               <div className={styles.input}>
                                 <Input control={control}
                                        name={"email"}
                                        disabled={!!user?.id && !isTestToolAdmin}
                                        maxLength={100}
                                        error={!!errors.email ? errors.email.message : ""}/>
                               </div>
                             </div>
                             {needChangePassword ? (
                               <div className={styles.inputContainer}>
                                 <span className={styles.required}>
                                   {!!user?.id ? t("user.newPassword") : t("login.password")}
                                 </span>
                                 <div className={styles.inputSubContainer}>
                                   <div className={`${styles.input} ${user?.id ? styles.password : ""}` }>
                                     <Input control={control}
                                            name={"password"}
                                            maxLength={100}
                                            error={!!errors.password ? errors.password.message : ""}/>
                                   </div>
                                   {!!user?.id && (
                                     <div className={styles.action} onClick={() => setNeedChangePassword(false)}>
                                       <img src={removeBlockIcon} alt=""/>
                                     </div>
                                   )}

                                 </div>
                               </div>
                             ) : isTestToolAdmin && (
                               <button className={styles.changePassword}
                                       onClick={() => setNeedChangePassword(true)}>
                                 {t("user.changePassword")}
                               </button>
                             )}
                           </div>
                           <div className={styles.projects}>
                             <div className={styles.title}>
                               {t("main.project")}
                               { !!firstFreeProject &&
                                 <div className={styles.action}
                                      onClick={addProjectClick}>
                                   <img src={addBlockIcon} alt=""/>
                                 </div>
                               }
                             </div>
                             <div className={styles.header}>
                               <div className={styles.name}>{t("login.name")}</div>
                               <div className={styles.role}>{t("admin.role")}</div>
                               <div className={styles.active}>{t("admin.active")}</div>
                               <div className={styles.expirationDate}>{t("user.expirationDate")}</div>
                             </div>
                             {projectFields.map((projectField, index) => {
                               const isEnabled = isEnabledEditProject(index);
                               return (
                                 <div className={styles.inputs} key={projectField.id}>
                                   <div className={styles.inputContainer}>
                                     <div className={`${styles.input} ${styles.name}`}>
                                       <Dropdown items={projectDropdowns[index] || []}
                                                 name={`projects.${index}.projectId`}
                                                 disabled={!isEnabled}
                                                 control={control}/>
                                     </div>
                                   </div>
                                   <div className={styles.inputContainer}>
                                     <div className={`${styles.input} ${styles.role}`}>
                                       <Dropdown items={rolesDropdown}
                                                 showCleanField={isTestToolAdmin}
                                                 error={errors.projects?.[index]?.roleId ? errors.projects[index]?.roleId?.message : ""}
                                                 name={`projects.${index}.roleId`}
                                                 disabled={!isEnabled}
                                                 control={control}/>
                                     </div>
                                   </div>
                                   <div className={styles.checkboxContainer}>
                                     <CheckboxHookForm
                                       control={control}
                                       disabled={!isEnabled}
                                       name={`projects.${index}.isActive`}
                                     />
                                   </div>
                                   <div className={styles.inputContainer}>
                                     <div className={`${styles.input} ${styles.expirationDate}`}>
                                       <DateInput disabled={!isEnabled}
                                                  control={control}
                                                  name={`projects.${index}.passwordExpirationDate`}/>
                                     </div>
                                   </div>
                                   <div className={styles.action}>
                                     {isEnabled && projectFields.length !== 1 && (
                                       <img src={removeBlockIcon} alt="" onClick={() => onClickRemoveProject(index)}/>
                                     )}
                                   </div>
                                 </div>
                               )
                             })}
                           </div>
                         </div>
                       </div>
                       <div className={styles.buttonsContainer}>
                         <div className={styles.buttons}>
                           {isTestToolAdmin && !!user && (
                             <button className={styles.delete}
                                     disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                     onClick={onDeleteClick}>{t("common.delete")}</button>
                           )}
                         </div>
                         <div className={styles.buttons}>
                           <button className={styles.cancel}
                                   disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                   onClick={() => onClose(false)}>{t("common.cancel")}</button>
                           <button className={styles.save}
                                   disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                   onClick={onClickSave}>{t("common.save")}</button>
                         </div>
                       </div>

                     </div>
                   }/>
      {showDeleteMessage && (
        <MessageWindow onClose={closeMessageWindow}
                       title={t("user.delete")}
                       description={t("user.deleteDescription")}
                       primaryText={t("common.ok")} secondaryText={t("common.cancel")}/>
      )}
      {
        showErrorMessage && (
          <MessageWindow onClose={closeErrorMessage}
                         title={t("common.serverError")}
                         description={t("common.serverErrorDescription")}
                         primaryText={t("common.ok")} />
        )
      }
    </>

  );
}